/*
 * Account drawer: style the <shop-login-button> shadow DOM.
 *
 * Shopify renders <shop-login-button> with an open shadow root and uses
 * adoptedStyleSheets internally. External CSS cannot reach inside, so we
 * push a CSSStyleSheet into its shadowRoot.adoptedStyleSheets here.
 *
 * The internal markup (button > span > svg) is not a public API and can
 * change at any time — keep this override sheet small and easy to update.
 */
(function () {
  var SHEET_KEY = '__branchAccountDrawerSheet';
  var STYLE_EL_KEY = '__branchAccountDrawerStyleEl';
  var HOOKED_KEY = '__branchAccountDrawerHooked';

  var CSS = [
    'button {',
    '  background-color: var(--color-primary, #314438) !important;',
    '  border: 2px solid var(--color-primary, #314438) !important;',
    '  border-radius: var(--border-radius, 0) !important;',
    '  width: 100% !important;',
    '  min-height: 40px !important;',
    '  padding: 14.75px !important;',
    '}',
    'button:hover {',
    '  background-color: transparent !important;',
    '}',
    'button > span {',
    '  background-color: transparent !important;',
    '  color: var(--color-white, #fff) !important;',
    '  font-weight: var(--font-weight-medium, 500) !important;',
    '  min-height: 0 !important;',
    '  padding: 0 !important;',
    '  font-family: Quicksand, sans-serif !important;',
    '  font-size: 14px !important;',
    '  text-transform: uppercase !important;',
    '  letter-spacing: .05em !important;',
    '  font-weight: 700 !important;',
    '  line-height: 1 !important;',
    '}',
    'button:hover span {',
    '  color: var(--color-primary, #314438) !important;',
    '}',
    'svg {',
    '  color: currentColor !important;',
    '  width: 20px !important;',
    '  height: 20px !important;',
    '  width: 50px !important;',
    '  height: 20px !important;',
    '  margin-top: -2px !important;',
    '}',
  ].join('\n');

  function applyStyles(el) {
    var root = el && el.shadowRoot;
    if (!root) return false;

    // Try adoptedStyleSheets first; cache our sheet per-element so we can
    // re-add it if the SDK reassigns root.adoptedStyleSheets to its own array.
    try {
      var sheet = el[SHEET_KEY];
      if (!sheet) {
        sheet = new CSSStyleSheet();
        sheet.replaceSync(CSS);
        el[SHEET_KEY] = sheet;
      }
      if (root.adoptedStyleSheets.indexOf(sheet) === -1) {
        root.adoptedStyleSheets = root.adoptedStyleSheets.concat(sheet);
      }
      return true;
    } catch (e) {
      // Fallback: append a <style> element. Re-append if the SDK wiped it.
      var styleEl = el[STYLE_EL_KEY];
      if (!styleEl) {
        styleEl = document.createElement('style');
        styleEl.textContent = CSS;
        el[STYLE_EL_KEY] = styleEl;
      }
      if (styleEl.parentNode !== root) root.appendChild(styleEl);
      return true;
    }
  }

  function hookElement(el) {
    if (el[HOOKED_KEY]) return;
    el[HOOKED_KEY] = true;

    applyStyles(el);

    // Re-apply whenever the SDK mutates the shadow tree (it hydrates async
    // and can replace inner DOM and/or reassign adoptedStyleSheets).
    if (el.shadowRoot) {
      new MutationObserver(function () { applyStyles(el); })
        .observe(el.shadowRoot, { childList: true, subtree: true });
    }

    // SDK emits a 'loaded' event when the feature element finishes loading.
    el.addEventListener('loaded', function () { applyStyles(el); });

    // Safety net: poll for ~2s to catch any signal we missed.
    var ticks = 0;
    var iv = setInterval(function () {
      applyStyles(el);
      if (++ticks >= 20) clearInterval(iv);
    }, 100);
  }

  function scan(scope) {
    (scope || document).querySelectorAll('shop-login-button').forEach(hookElement);
  }

  scan();

  // Re-scan once the custom element class is registered by the Shop SDK —
  // this is the moment shadow content is most likely to have finalized.
  if (window.customElements && customElements.whenDefined) {
    customElements.whenDefined('shop-login-button').then(function () {
      scan();
    }).catch(function () {});
  }

  new MutationObserver(function (records) {
    for (var i = 0; i < records.length; i++) {
      var added = records[i].addedNodes;
      for (var j = 0; j < added.length; j++) {
        var n = added[j];
        if (n.nodeType !== 1) continue;
        if (n.matches && n.matches('shop-login-button')) hookElement(n);
        if (n.querySelectorAll) scan(n);
      }
    }
  }).observe(document.documentElement, { childList: true, subtree: true });
})();

/*
 * The mobile drawer is scoped to the title bar: the slide-down header holds a
 * CLONE with the same .account-drawer class whose panel is display:none and
 * whose open class is never toggled (see assets/scripts_snippets_account.js,
 * which scopes its own lookup the same way).
 */
function branchAccountMobileDrawer() {
  var titleBar = document.querySelector('.toggle-header__title-bar');
  return (titleBar && titleBar.querySelector('.account-drawer')) || document.querySelector('.account-drawer');
}

/*
 * Account drawer: fetch the drawer body on open-intent (signed-in shoppers).
 *
 * The page ships a skeleton (see account-actions.liquid); the real body —
 * welcome block, latest order, wishlist, recommendations, recently viewed —
 * arrives in ONE Section Rendering request:
 *   GET {current path}?sections=account-content
 *
 * Fetched against the current path rather than /, so the section renders with
 * the page's own context: `product` is the PDP being viewed (Recently Viewed
 * excludes it in Liquid) and `cart` is the live cart (so the recommendation
 * seed reflects an add-to-cart that happened without a navigation).
 *
 * The fetch starts on the same hover / focus / touch intent that used to warm
 * the per-card requests, so it usually lands before the panel finishes sliding
 * in. Nothing is cached across page loads on purpose — the body carries
 * customer data and sessionStorage is the wrong place for it — but the in-flight
 * promise is shared, so the desktop flyout and the mobile drawer are filled by
 * a single request.
 */
(function () {
  var SECTION = 'account-content';
  var pending = null;   // Promise<bodyHTML> for this page load
  var inFlight = false; // a load() is waiting on that promise
  var filled = false;   // the body has been written into the containers
  var stale = false;    // cart changed since the last fetch

  var DRAWERS = [
    { el: document.getElementById('side-account'), openClass: 'open' },
    { el: branchAccountMobileDrawer(), openClass: 'account-drawer--open' }
  ].filter(function (d) { return d.el; });

  // Only signed-in shoppers get a skeleton — logged-out content is inlined, so
  // there is nothing to fetch.
  function skeletonPresent() {
    return !!document.querySelector('[data-account-body-pending]');
  }

  // We own the containers while the page-rendered skeleton is still there, or
  // once we've filled them ourselves. If a live "Sign in with Shop" login swapped
  // its own markup in first, that content wins and we stay out of the way.
  function owns() {
    return skeletonPresent() || filled;
  }

  // Section Rendering wraps output in #shopify-section-<id>; we want its
  // children, matching what scripts_account_live_login.js swaps in.
  function extractBody(html) {
    if (!html) return '';
    var doc = new DOMParser().parseFromString(html, 'text/html');
    var section = doc.querySelector('[id^="shopify-section-"]') || doc.body;
    return section ? section.innerHTML : '';
  }

  function requestBody(path) {
    return fetch(path + '?sections=' + SECTION, { credentials: 'same-origin', cache: 'no-store' })
      .then(function (res) { return res.ok ? res.json() : null; })
      .then(function (json) { return json ? extractBody(json[SECTION] || '') : ''; });
  }

  // Falls back to the root path when the current one can't render the section
  // (a 404, a route the section API rejects). That loses the `product` context
  // but still fills the drawer.
  function fetchBody() {
    if (pending) return pending;
    pending = requestBody(window.location.pathname)
      .then(function (html) { return html || requestBody('/'); })
      .catch(function () { return requestBody('/').catch(function () { return ''; }); })
      .then(function (html) {
        if (!html) pending = null; // let a later intent retry
        return html;
      });
    return pending;
  }

  function load() {
    if (inFlight || !owns()) return;
    if (!skeletonPresent() && !stale) return; // already filled and still fresh
    if (stale) { pending = null; stale = false; }
    inFlight = true;
    fetchBody().then(function (html) {
      inFlight = false;
      if (!html || !owns()) return;
      document.querySelectorAll('[data-account-content]').forEach(function (dest) {
        dest.innerHTML = html;
      });
      filled = true;
      document.dispatchEvent(new CustomEvent('account-content:updated'));
    });
  }

  function onIntent(event) {
    var target = event.target;
    if (target && target.closest && target.closest('[data-side-account-trigger]')) load();
  }
  document.addEventListener('mouseover', onIntent, { passive: true });
  document.addEventListener('focusin', onIntent);
  document.addEventListener('touchstart', onIntent, { passive: true });

  // Catch opens that skipped the intent listeners — a programmatic open, or the
  // breakpoint hand-off between the flyout and the mobile drawer.
  DRAWERS.forEach(function (d) {
    if (d.el.classList.contains(d.openClass)) {
      load();
    } else {
      new MutationObserver(function () {
        if (d.el.classList.contains(d.openClass)) load();
      }).observe(d.el, { attributes: true, attributeFilter: ['class'] });
    }
  });

  // The body embeds a cart-derived recommendation seed and filters out products
  // already in the cart, so a cart change makes it stale. The wishlist block is
  // rendered from the customer metafield, so a heart toggle does too — the PDP
  // heart is expected to dispatch 'wishlist:updated' on window after the proxy
  // confirms the write. Either way we refetch on the next open rather than
  // swapping content out from under an open drawer.
  window.addEventListener('cart:updated', function () { stale = true; });
  window.addEventListener('wishlist:updated', function () { stale = true; });

  // A live login swaps its own body in; from then on refreshes are ours again.
  document.addEventListener('account-content:updated', function () { filled = true; });
})();

/*
 * Account drawer: initialize the sliders in the fetched body.
 *
 * Recently Viewed cards are rendered by Liquid inside account-body, so there is
 * nothing to load here — only Swiper to attach once the body is in the DOM.
 * (Recommended For You has to fetch; it owns its own slider, below.)
 */
(function () {
  var SLIDERS = [
    { slider: '[data-recently-viewed-slider]', prev: '[data-recently-viewed-prev]', next: '[data-recently-viewed-next]' },
    { slider: '[data-wishlist-slider]', prev: '[data-wishlist-prev]', next: '[data-wishlist-next]' },
    // Recommended builds its own slider when its cards land, but it needs to be
    // in the repair pass too: if those cards arrive while the column is hidden
    // behind the wishlist layer, it initializes with no layout and collapses.
    { slider: '[data-recommended-slider]', prev: '[data-recommended-prev]', next: '[data-recommended-next]' }
  ];

  function init(config, scope) {
    var el = scope.querySelector(config.slider);
    if (!el) return;
    if (!el.querySelector('.swiper-slide')) return; // nothing rendered for this one
    // Swiper measures on init, so skip anything with no layout — the cloned
    // slide-down header's panel is display:none, and the menu column is hidden
    // while the wishlist layer is open. Closed drawers still have layout
    // (they're translated / offscreen), so they initialize fine.
    if (!el.offsetWidth) return;

    // Already built: re-measure rather than bail. autoHeight latches at 0 when a
    // slider first measures while its column is hidden, and it stays collapsed
    // forever unless something asks it to measure again — that is what left the
    // menu looking empty after pressing Back. update() alone doesn't always
    // recover it, so rebuild rather than leave an empty row on screen.
    if (el.swiper) {
      el.swiper.update();
      var wrapper = el.querySelector('.swiper-wrapper');
      if (!wrapper || wrapper.clientHeight > 0) return;
      el.swiper.destroy(true, true);
    }
    if (typeof window.Swiper === 'undefined') {
      if (typeof window.whenSwiperReady === 'function') {
        window.whenSwiperReady(function () { init(config, scope); });
      }
      return;
    }
    new window.Swiper(el, {
      slidesPerView: 2.2,
      spaceBetween: 12,
      autoHeight: true,
      navigation: {
        prevEl: scope.querySelector(config.prev),
        nextEl: scope.querySelector(config.next)
      }
    });
  }

  function initAll() {
    document.querySelectorAll('[data-account-content]').forEach(function (scope) {
      SLIDERS.forEach(function (config) { init(config, scope); });
    });
  }

  document.addEventListener('account-content:updated', initAll);
  // The menu's sliders are display:none behind the layer, so anything that
  // measured while it was open is collapsed. Re-measure as it closes.
  document.addEventListener('account-layer:closed', initAll);

  // Retry on open for anything that had no layout when the body landed.
  [
    { el: document.getElementById('side-account'), openClass: 'open' },
    { el: branchAccountMobileDrawer(), openClass: 'account-drawer--open' }
  ].forEach(function (d) {
    if (!d.el) return;
    new MutationObserver(function () {
      if (d.el.classList.contains(d.openClass)) initAll();
    }).observe(d.el, { attributes: true, attributeFilter: ['class'] });
  });

  initAll(); // no-op until a body is in place
})();

/*
 * Account drawer: the second layer (currently the wishlist).
 *
 * [data-account-layer-open="wishlist"] slides [data-account-layer="wishlist"]
 * in from the right; [data-account-layer-close] takes it back. Both layers live
 * inside the same [data-account-content] wrapper, so this works in the desktop
 * flyout and the mobile drawer without knowing which is which — and listeners
 * are delegated on document, so the markup arriving from the body fetch needs
 * no re-binding.
 *
 * Opening adds a class that hides the layer's siblings, which is what keeps the
 * layer in normal flow (see the CSS note). Closing has to undo that before the
 * slide-out, or the layer would animate away over a blank panel — so it puts the
 * menu back and lifts the layer out of flow (position: absolute, pinned to the
 * top of the container) for the duration of the animation. The layer stays
 * exactly where it was painted and slides off over the restored menu.
 */
(function () {
  var CONTAINER_OPEN = 'side-account--layer-open';
  var CONTAINER_CLOSING = 'side-account--layer-closing';
  var LAYER_OPEN = 'side-account__layer--open';
  var LAYER_CLOSING = 'side-account__layer--closing';
  var RETURN_FOCUS = '__branchLayerReturnFocus';
  var CLOSE_TIMER = '__branchLayerCloseTimer';
  var LAYER_CLOSE_MS = 280;  // keep in sync with the CSS transition duration
  var DRAWER_CLOSE_MS = 400; // matches the flyout's slide-out

  // Under reduced motion the CSS drops the transition, so there's nothing to
  // wait for — tear down immediately instead of holding an invisible layer.
  function closeDuration() {
    var query = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)');
    return query && query.matches ? 0 : LAYER_CLOSE_MS;
  }

  function endClosing(layer) {
    window.clearTimeout(layer[CLOSE_TIMER]);
    layer[CLOSE_TIMER] = null;
    layer.classList.remove(LAYER_CLOSING);
    var container = containerOf(layer);
    if (container) container.classList.remove(CONTAINER_CLOSING);
  }

  function containerOf(el) {
    return el.closest('[data-account-content]');
  }

  function openLayer(trigger) {
    var container = containerOf(trigger);
    if (!container) return;
    var name = trigger.getAttribute('data-account-layer-open');
    var layer = container.querySelector('[data-account-layer="' + name + '"]');
    if (!layer) return; // nothing saved yet — the tab isn't rendered, but be safe

    // Reopening mid-close: cancel the teardown and drop back into flow before
    // the pending timeout can hide the layer we're about to show.
    endClosing(layer);

    layer[RETURN_FOCUS] = trigger;
    layer.hidden = false;
    container.classList.add(CONTAINER_OPEN);
    container.scrollTop = 0;
    // Next frame, so the layer has a painted starting transform to animate from.
    requestAnimationFrame(function () { layer.classList.add(LAYER_OPEN); });
    // preventScroll: the layer starts translated fully off to the right, and
    // focusing something offscreen would scroll the drawer sideways to reach it.
    var back = layer.querySelector('[data-account-layer-close]');
    if (back) back.focus({ preventScroll: true });
  }

  function closeLayer(layer, restoreFocus) {
    // Already gone, or already sliding out — its own timer owns the teardown.
    if (layer.hidden || layer.classList.contains(LAYER_CLOSING)) return;
    var container = containerOf(layer);

    // Put the menu back and pin the layer over it in the same frame, so it
    // slides off across real content rather than an empty panel. Only the
    // transform changes, so the slide-out animates from wherever it sits now.
    if (container) {
      container.classList.remove(CONTAINER_OPEN);
      container.classList.add(CONTAINER_CLOSING);
      container.scrollTop = 0;
    }
    layer.classList.add(LAYER_CLOSING);
    layer.classList.remove(LAYER_OPEN);

    // The menu is back in flow as of the lines above, so its sliders have
    // layout again and can be re-measured before the slide-out finishes.
    document.dispatchEvent(new CustomEvent('account-layer:closed'));

    var trigger = layer[RETURN_FOCUS];
    if (restoreFocus !== false && trigger && trigger.isConnected) {
      trigger.focus({ preventScroll: true });
    }

    window.clearTimeout(layer[CLOSE_TIMER]);
    layer[CLOSE_TIMER] = window.setTimeout(function () {
      layer.hidden = true;
      endClosing(layer);
    }, closeDuration());
  }

  function openLayers() {
    return document.querySelectorAll('[data-account-layer]:not([hidden])');
  }

  document.addEventListener('click', function (event) {
    var target = event.target;
    if (!target || !target.closest) return;

    var opener = target.closest('[data-account-layer-open]');
    if (opener) {
      event.preventDefault();
      openLayer(opener);
      return;
    }

    var closer = target.closest('[data-account-layer-close]');
    if (closer) {
      event.preventDefault();
      var layer = closer.closest('[data-account-layer]');
      if (layer) closeLayer(layer);
    }
  });

  // Escape backs out of the layer instead of closing the whole drawer. Captured
  // on document so it runs before the drawer's own bubble-phase Escape handlers
  // (simplistic.js.liquid for the flyout, scripts_snippets_account.js for the
  // mobile drawer) and stops the event from reaching them.
  document.addEventListener('keydown', function (event) {
    if (event.key !== 'Escape') return;
    var layers = openLayers();
    if (!layers.length) return;
    event.stopPropagation();
    layers.forEach(function (layer) { closeLayer(layer); });
  }, true);

  // Closing the drawer resets it to the top layer, so reopening never lands
  // mid-stack. Deferred past the slide-out so the menu doesn't flash back in
  // while the panel is still on screen, and focus is left alone — the drawer's
  // own close handler owns it at that point.
  function resetAfterClose() {
    var layers = openLayers();
    if (!layers.length) return;
    setTimeout(function () {
      openLayers().forEach(function (layer) { closeLayer(layer, false); });
    }, DRAWER_CLOSE_MS);
  }

  [
    { el: document.getElementById('side-account'), openClass: 'open' },
    { el: branchAccountMobileDrawer(), openClass: 'account-drawer--open' }
  ].forEach(function (d) {
    if (!d.el) return;
    new MutationObserver(function () {
      if (!d.el.classList.contains(d.openClass)) resetAfterClose();
    }).observe(d.el, { attributes: true, attributeFilter: ['class'] });
  });

  // A body swap replaces the container's children but not the container itself,
  // so a stale CONTAINER_OPEN class would hide every child of the fresh markup
  // and leave the drawer blank. The new body always ships with its layer hidden,
  // so clear the flag whenever content lands.
  document.addEventListener('account-content:updated', function () {
    document.querySelectorAll('[data-account-content]').forEach(function (container) {
      container.classList.remove(CONTAINER_OPEN);
      container.classList.remove(CONTAINER_CLOSING);
    });
  });
})();

/*
 * Account drawer: composite render thumbnails for the wishlist.
 *
 * Some products (Duo Standing Desk and friends) don't have one uploaded image
 * per variant — their thumbnail is composed from two layered renders, a top
 * colour over a base colour. Newer variants frequently have no variant image at
 * all, so Liquid's card falls back to product.featured_image: the lifestyle
 * shot. A shopper who saved "Denim / Eggshell" then sees a styled room photo
 * instead of the desk they picked.
 *
 * Product tiles solve this with updateStaticTile(), but that hunts for
 * .product-tile__image-container and friends, which the drawer cards don't have.
 * So this uses the same two primitives underneath —
 * returnRelevantCompositeImagesForVariant() to match layers to the variant, and
 * buildCompositeLayersHTML() to render them — and drops the result into the
 * card's own image slot. Same matching, same markup, no fork of the logic.
 *
 * Liquid tags eligible slots with the variant's option VALUES and the product's
 * option NAMES; the resolver needs both to pair a variant to its layers.
 */
(function () {
  function compositeFor(slot) {
    var api = window.Branch && window.Branch.compositeRenderImage;
    if (!api || typeof api.returnRelevantCompositeImagesForVariant !== 'function') return;
    if (slot.getAttribute('data-composite-applied') === 'true') return;

    var productId = parseInt(slot.getAttribute('data-composite-product-id'), 10);
    var options, names;
    try {
      options = JSON.parse(slot.getAttribute('data-composite-variant-options'));
      names = JSON.parse(slot.getAttribute('data-composite-option-names'));
    } catch (e) {
      return;
    }
    if (!productId || !options) return;

    // The resolver only reads .options off the variant, so a literal is enough —
    // no need to ship the whole variant object into the drawer body.
    var layers = api.returnRelevantCompositeImagesForVariant({ options: options }, productId, null, names, slot);
    if (!layers || layers.length < 2) return;

    var front = layers.filter(function (img) { return img.dimensions_overlay_view === 'front'; });
    if (front.length < 2) return;

    // Mark before mutating: a second pass must not stack another set of layers.
    slot.setAttribute('data-composite-applied', 'true');

    var original = slot.querySelector('img');
    if (original) original.style.display = 'none';

    var wrapper = document.createElement('div');
    wrapper.className = 'side-account__composite';
    wrapper.innerHTML = api.buildCompositeLayersHTML(front[0], front[1], {
      widths: [90, 180, 360],
      defaultWidth: 180,
      loading: 'lazy',
      altPrefix: (options.join(', ') || '') + ' - ',
      style: 'position:absolute;top:0;left:0;width:100%;height:100%;object-fit:contain;'
    });

    // Sit inside the existing link when there is one, so the thumbnail keeps
    // taking the shopper to the product.
    (slot.querySelector('a') || slot).appendChild(wrapper);
  }

  function applyAll() {
    document
      .querySelectorAll('[data-account-content] [data-composite-product-id]')
      .forEach(compositeFor);
  }

  document.addEventListener('account-content:updated', applyAll);
  document.addEventListener('account-layer:closed', applyAll);
  applyAll(); // no-op until a body is in place
})();

/*
 * Account drawer: campaign pricing for the wishlist cards.
 *
 * Liquid renders these prices at full value — return_discount_price does not
 * apply the active campaign, so the PDP and every product tile arrive at full
 * price too and are corrected on the client. This does the same for the drawer,
 * through the very same calculator the tiles use
 * (window.Branch.functions.returnDiscountCodeUpdates), so a wishlist card shows
 * exactly what the same product shows anywhere else — including tiered
 * discounts, campaign 2, and campaigns configured not to strike through.
 *
 * The drawer body arrives after page load, so nothing else ever gets the chance
 * to price these; re-run whenever a body lands and whenever the layer reopens.
 */
(function () {
  function formatMoney(cents) {
    if (window.Shopify && typeof Shopify.formatMoney === 'function') {
      return Shopify.formatMoney(cents, window.Simplistic && Simplistic.formatMoney)
        .replace('.00', '')
        .replace(' ', '');
    }
    return '$' + Math.round(cents / 100);
  }

  function priceCard(priceEl) {
    if (priceEl.getAttribute('data-campaign-priced') === 'true') return;

    var fn = window.Branch && window.Branch.functions && window.Branch.functions.returnDiscountCodeUpdates;
    if (typeof fn !== 'function') return; // reusable-functions.js not in yet

    var handle = priceEl.getAttribute('data-product-handle');
    var dealWrap = priceEl.querySelector('.deal');
    var retailWrap = priceEl.querySelector('.retail');
    var dealSpan = priceEl.querySelector('span[data-price="deal"]');
    var retailSpan = priceEl.querySelector('span[data-price="retail"]');
    if (!handle || !dealWrap || !dealSpan) return;

    var current = parseInt(dealWrap.getAttribute('data-price'), 10);
    var original = parseInt(retailWrap && retailWrap.getAttribute('data-price'), 10);
    if (!current) return;
    if (!original || original < current) original = current;

    var result = fn(current, original, 0, '', handle) || {};
    var newCurrent = Math.round(result.currentPrice);
    var newOriginal = Math.round(result.originalPrice || original);
    priceEl.setAttribute('data-campaign-priced', 'true');
    if (!(newCurrent < newOriginal)) return; // nothing to show

    dealSpan.innerHTML = formatMoney(newCurrent);
    if (retailWrap && retailSpan) {
      retailSpan.innerHTML = formatMoney(newOriginal);
      retailWrap.setAttribute('data-visible', 'true');
      retailWrap.setAttribute('data-price', newOriginal);
    }

    var message = priceEl.querySelector('.price__discount-message');
    if (message && result.discountMessage) {
      message.innerHTML = result.discountMessage;
      message.setAttribute('data-visible', 'true');
    }
  }

  function priceAll() {
    document
      .querySelectorAll('[data-account-content] .side-account__wishlist-item-price .price')
      .forEach(priceCard);
  }

  document.addEventListener('account-content:updated', priceAll);
  document.addEventListener('account-layer:closed', priceAll);
  priceAll(); // no-op until a body is in place
})();

/*
 * Account drawer: the wishlist layer's actions — "Add all to cart", a card's
 * "Move to Cart", and a card's remove (trash).
 *
 * Cart adds go through the theme's canonical path: POST /cart/add.js with
 * {items}, then window.Branch.cart.updateCartDesc(true), which rebuilds
 * #side-cart from /cart?view=side-cart and opens it (same sequence as
 * addFreeGiftMerch in scripts_sections_cart.js and the related-products
 * carousel). The account panel is closed first: #side-cart and #side-account
 * share z-index 9999 and the drawer is rendered later in theme.liquid, so the
 * cart would open behind it.
 *
 * "Add all to cart" carries the variant ids Liquid rendered into it, so it adds
 * exactly the cards on screen, and only ones that were purchasable at render
 * time. Anything that sold out since comes back as a 422, whose `description` is
 * the message Shopify would show on the PDP — so that's what we surface.
 *
 * Unsaving goes to the Branch App Proxy, which owns the metafield. The wishlist
 * holds VARIANT references, so every action keys off data-product-variant-id — a
 * product id can't address an entry in it. The proxy's response carries the whole
 * list, so the count comes from there rather than from counting what's left on
 * screen, and it stays right even if this copy of the body was already stale.
 *
 * All delegated on document, because these cards arrive from the body fetch.
 */
(function () {
  var ENDPOINT = '/apps/branch/wishlist';
  var BUSY_LABEL = 'Adding…';
  var GENERIC_ERROR = 'Something went wrong — please try again.';

  function closeAccountPanels() {
    var flyout = document.getElementById('side-account');
    if (flyout && flyout.classList.contains('open') && typeof window.hideSideAccount === 'function') {
      window.hideSideAccount();
    }
    var drawer = branchAccountMobileDrawer();
    if (drawer && drawer.classList.contains('account-drawer--open')) {
      // closeDrawer() in scripts_snippets_account.js isn't exposed, so go
      // through the button it's bound to and keep using the theme's own path.
      var close = drawer.querySelector('.account-drawer__close-button');
      if (close) close.click();
    }
  }

  // The layer's status line is shared by all three actions: a card has nowhere
  // of its own to put a message, so look outward for the nearest one.
  function errorEl(el) {
    var scope = el.closest('[data-account-layer], [data-wishlist]') || el.parentElement;
    return scope ? scope.querySelector('[data-wishlist-add-error]') : null;
  }

  function showError(el, message) {
    var target = errorEl(el);
    if (!target) return;
    target.textContent = message;
    target.hidden = false;
  }

  function clearError(el) {
    var target = errorEl(el);
    if (!target) return;
    target.textContent = '';
    target.hidden = true;
  }

  function contentScopes() {
    return document.querySelectorAll('[data-account-content]');
  }

  function addToCart(items) {
    var root = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
    return fetch(root + 'cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ items: items })
    }).then(function (res) {
      return res.json().then(function (body) {
        if (!res.ok) throw body;
        return body;
      });
    });
  }

  // Matches the dispatch in scripts_sections_cart.js; the drawer's body loader
  // listens for it and refetches on the next open, so the recommendation seed
  // and in-cart filtering pick up the new items.
  function afterCartAdd(cart) {
    window.dispatchEvent(new CustomEvent('cart:updated', { detail: cart }));

    // No #side-cart on the cart template (see theme.liquid) — there, a reload
    // is the only way to show what was just added.
    if (!document.getElementById('side-cart')) {
      window.location.reload();
      return;
    }
    closeAccountPanels();
    if (window.Branch && window.Branch.cart && typeof window.Branch.cart.updateCartDesc === 'function') {
      window.Branch.cart.updateCartDesc(true);
    }
  }

  function unsave(variantIds) {
    return fetch(ENDPOINT, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ action: 'remove', variantIds: variantIds })
    })
      .then(function (res) { return res.json().catch(function () { return null; }); })
      .then(function (data) {
        if (!data || data.ok !== true) throw new Error('wishlist remove failed');
        // The PDP heart listens for this and un-fills for the same variant.
        window.dispatchEvent(
          new CustomEvent('wishlist:updated', {
            detail: { variantIds: data.variantIds, count: data.count }
          })
        );
        return data;
      });
  }

  // One variant can be on screen twice — once in the slider preview, once in the
  // layer's grid — and twice again because the desktop flyout and the mobile
  // drawer hold separate copies of the same fetched body.
  function cardsFor(variantId) {
    var out = [];
    contentScopes().forEach(function (scope) {
      scope
        .querySelectorAll('[data-product-variant-id="' + variantId + '"]')
        .forEach(function (el) {
          var card = el.closest('.swiper-slide') || el.closest('.side-account__wishlist-item');
          if (card && out.indexOf(card) === -1) out.push(card);
        });
    });
    return out;
  }

  function setCount(count) {
    contentScopes().forEach(function (scope) {
      scope.querySelectorAll('[data-wishlist-count]').forEach(function (el) {
        el.textContent = count + ' saved ' + (count === 1 ? 'item' : 'items');
      });
    });
  }

  // Last one gone: drop the slider and the layer tab, restore the empty copy,
  // and bail out of the layer if the shopper is standing in it.
  function showEmptyState() {
    contentScopes().forEach(function (scope) {
      var section = scope.querySelector('[data-wishlist]');
      if (section) {
        var products = section.querySelector('.side-account__products');
        if (products) products.remove();

        var tab = section.querySelector('[data-account-layer-open="wishlist"]');
        if (tab) tab.remove();

        // A wishlist-less page prints no count at all, so drop "0 saved items".
        var count = section.querySelector('[data-wishlist-count]');
        if (count) count.remove();

        if (!section.querySelector('[data-wishlist-empty]')) {
          // The section ships the empty copy as a template so the markup here
          // matches what a wishlist-less page renders.
          var wishlistTemplate = section.querySelector('[data-wishlist-empty-template]');
          if (wishlistTemplate && wishlistTemplate.content) {
            section.appendChild(wishlistTemplate.content.cloneNode(true));
            wishlistTemplate.remove();
          } else {
            var note = document.createElement('p');
            note.className = 'side-account__wishlist-empty';
            note.setAttribute('data-wishlist-empty', '');
            note.textContent = 'Tap the heart on any product to save it here.';
            section.appendChild(note);
          }
        }
      }

      var layer = scope.querySelector('[data-account-layer="wishlist"]');
      if (!layer) return;
      if (!layer.hasAttribute('hidden')) {
        var close = layer.querySelector('[data-account-layer-close]');
        if (close) close.click(); // reuse the layer's own close path
      }
      layer.remove();
    });
  }

  // Only ever update a slider that has layout. Updating one while it is hidden
  // behind the layer makes autoHeight collapse it to zero.
  function refreshSliders() {
    contentScopes().forEach(function (scope) {
      var el = scope.querySelector('[data-wishlist-slider]');
      if (el && el.swiper && el.offsetWidth) el.swiper.update();
    });
  }

  function applyRemoval(cards, count) {
    cards.forEach(function (card) { card.remove(); });
    setCount(count);
    if (count === 0) showEmptyState();
    refreshSliders();
  }

  document.addEventListener('click', function (event) {
    var target = event.target;
    if (!target || !target.closest) return;

    // --- remove (trash) ---------------------------------------------------
    var removeBtn = target.closest('[data-wishlist-remove]');
    if (removeBtn) {
      event.preventDefault();
      if (removeBtn.getAttribute('data-busy') === 'true') return;

      var removeId = removeBtn.getAttribute('data-product-variant-id');
      if (!removeId) return;

      var removeCards = cardsFor(removeId);
      removeBtn.setAttribute('data-busy', 'true');
      clearError(removeBtn);
      removeCards.forEach(function (card) { card.hidden = true; });

      unsave([removeId])
        .then(function (data) {
          applyRemoval(removeCards, data.count);
        })
        .catch(function () {
          removeCards.forEach(function (card) { card.hidden = false; }); // put them back
          showError(removeBtn, GENERIC_ERROR);
        })
        .then(function () {
          removeBtn.removeAttribute('data-busy');
        });
      return;
    }

    // --- move to cart (one card) ------------------------------------------
    var moveLink = target.closest('[data-wishlist-move-to-cart]');
    if (moveLink) {
      event.preventDefault();
      if (moveLink.getAttribute('data-busy') === 'true') return;

      var moveId = moveLink.getAttribute('data-product-variant-id');
      if (!/^\d+$/.test(moveId || '')) return;

      moveLink.setAttribute('data-busy', 'true');
      clearError(moveLink);
      var moveLabel = moveLink.textContent;
      moveLink.textContent = BUSY_LABEL;

      addToCart([{ id: parseInt(moveId, 10), quantity: 1 }])
        .then(function (cart) {
          // "Move", so it leaves the wishlist — but only once it's safely in the
          // cart. If the unsave fails the item is simply still saved, which the
          // next drawer open corrects; losing it would be the worse outcome.
          return unsave([moveId])
            .catch(function () {})
            .then(function () { afterCartAdd(cart); });
        })
        .catch(function (err) {
          moveLink.textContent = moveLabel;
          showError(moveLink, (err && err.description) || GENERIC_ERROR);
        })
        .then(function () {
          moveLink.removeAttribute('data-busy');
        });
      return;
    }

    // --- add all to cart --------------------------------------------------
    var button = target.closest('[data-wishlist-add-all]');
    if (!button || button.disabled) return;
    event.preventDefault();

    var items = (button.getAttribute('data-wishlist-add-all') || '')
      .split(',')
      .filter(function (id) { return /^\d+$/.test(id); })
      .map(function (id) { return { id: parseInt(id, 10), quantity: 1 }; });
    if (!items.length) return;

    clearError(button);
    var label = button.textContent;
    button.disabled = true;
    button.textContent = BUSY_LABEL;

    // Adds only — the label says "Add", not "Move", and adding to cart isn't
    // buying. Purchased items leave the wishlist via the order-paid Flow.
    addToCart(items)
      .then(afterCartAdd)
      .catch(function (err) {
        button.disabled = false;
        button.textContent = label;
        showError(button, (err && err.description) || GENERIC_ERROR);
      });
  });
})();

/*
 * PDP: record the view against the signed-in customer's metafield via the
 * Branch App Proxy, so Recently Viewed follows them across devices. Signed-in
 * only, once per product per session, and fired on idle as a non-blocking,
 * fire-and-forget request so it never touches page render. The pre-existing
 * cookie write (jquery.products.js) is untouched and still powers the
 * logged-out PDP related-products carousel.
 */
(function () {
  // The skeleton only renders for signed-in shoppers; .side-account__details
  // covers the case where the body has already been swapped in.
  if (!document.querySelector('[data-account-body-pending], .side-account__details')) return;
  var data = document.getElementById('recently-viewed-data');
  if (!data) return; // not a product page
  var handle = data.getAttribute('data-product-handle');
  var id = data.getAttribute('data-product-id');
  if (!handle) return;
  try {
    var sent = JSON.parse(window.sessionStorage.getItem('rv-sent') || '[]');
    if (sent.indexOf(handle) !== -1) return;
    sent.push(handle);
    window.sessionStorage.setItem('rv-sent', JSON.stringify(sent));
  } catch (e) { /* sessionStorage full/disabled — still safe to send */ }
  var send = function () {
    fetch('/apps/branch/recently-viewed', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: id, handle: handle }),
      keepalive: true
    }).catch(function () {});
  };
  if (typeof window.requestIdleCallback === 'function') {
    window.requestIdleCallback(send, { timeout: 2000 });
  } else {
    setTimeout(send, 0);
  }
})();

/*
 * Account drawer: populate the "Recommended For You" slider.
 *
 * Native Shopify product recommendations, pulled through the Product
 * Recommendations API with section rendering so every card is still rendered by
 * Liquid (prices/images/locale come from the real `product` object):
 *   GET /recommendations/products?section_id=account-recommended&product_id={id}&limit=6&intent=related
 *
 * That API is product-seeded — there is no customer-level "recommended for you"
 * — so account-body resolves seeds in priority order into
 * [data-recommended-seeds] and we stop at the first that returns cards:
 *   1. most expensive item in the cart
 *   2. most expensive item in the last order
 *   3. most recently viewed product
 * No seed, or no seed that returns anything, leaves the block hidden (it ships
 * hidden, so an empty response is simply never revealed). Cascading past an
 * empty response also covers a seed whose product was deleted or has no
 * recommendations yet — e.g. a bundle.
 *
 * This runs after the drawer body lands, so it costs a second round trip behind
 * the body fetch. That's the price of the seeds living in the body; if it ever
 * shows, render the seeds into the page-load skeleton too and prefetch on intent.
 *
 * One request per seed, cached in sessionStorage. The cache key includes the
 * cart item count because the section filters out products already in the cart,
 * and both the seed and the count come from the same render of the body — so a
 * cached entry always matches what that render would have produced.
 */
(function () {
  var SECTION_ID = 'account-recommended';
  var LIMIT = 6;
  // 'related' is the algorithmic set. Switch to 'complementary' to use the
  // merchant-curated Complementary products metafield instead.
  var INTENT = 'related';
  // Versioned for the same reason as the recently-viewed cache above — bump on
  // any account-product-card.liquid change.
  var CACHE_PREFIX = 'rec-cards:2:';
  var cache = {}; // cacheKey -> Promise<cardsHTML>; dedupes in-flight + resolved fetches
  var prefetched = false;
  var loadedDrawers = new WeakSet();

  var DRAWERS = [
    { el: document.getElementById('side-account'), openClass: 'open' },
    { el: branchAccountMobileDrawer(), openClass: 'account-drawer--open' }
  ].filter(function (d) { return d.el; });

  // Deduped so a shopper whose cart and last order share a product doesn't
  // fetch the same seed twice.
  function seedsFor(section) {
    var seen = {};
    return (section.getAttribute('data-recommended-seeds') || '')
      .split(',')
      .map(function (s) { return s.trim(); })
      .filter(function (s) {
        if (!/^\d+$/.test(s) || seen[s]) return false;
        seen[s] = true;
        return true;
      });
  }

  // Strip the Section Rendering API wrapper (#shopify-section-…) and return the
  // slides' markup. '' when the seed has no recommendations to show.
  function extractCardsHTML(html) {
    if (!html) return '';
    var tmp = document.createElement('div');
    tmp.innerHTML = html;
    var cards = tmp.querySelector('[data-recommended-cards]');
    return cards ? cards.innerHTML.trim() : '';
  }

  function sessionGet(key) {
    try { return window.sessionStorage.getItem(key); } catch (e) { return null; }
  }

  function sessionSet(key, value) {
    try { window.sessionStorage.setItem(key, value); } catch (e) { /* full / disabled */ }
  }

  function fetchCardsHTML(seed, cartCount) {
    var key = CACHE_PREFIX + seed + ':' + cartCount;
    if (cache[key]) return cache[key];

    var stored = sessionGet(key);
    if (stored) {
      cache[key] = Promise.resolve(stored);
      return cache[key];
    }

    var root = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
    var url = root + 'recommendations/products?section_id=' + SECTION_ID +
      '&product_id=' + encodeURIComponent(seed) +
      '&limit=' + LIMIT +
      '&intent=' + INTENT;
    cache[key] = fetch(url)
      .then(function (res) { return res.ok ? res.text() : ''; })
      .then(extractCardsHTML)
      .then(function (cardsHTML) {
        if (cardsHTML) sessionSet(key, cardsHTML);
        return cardsHTML;
      })
      .catch(function () { return ''; });
    return cache[key];
  }

  // Walk the seed chain until one returns cards.
  function firstNonEmpty(seeds, cartCount, index) {
    index = index || 0;
    if (index >= seeds.length) return Promise.resolve('');
    return fetchCardsHTML(seeds[index], cartCount).then(function (cardsHTML) {
      if (cardsHTML) return cardsHTML;
      return firstNonEmpty(seeds, cartCount, index + 1);
    });
  }

  // Swiper loads on demand on the product template, so route through
  // whenSwiperReady when the bundle isn't ready yet (same as recently viewed).
  function initSlider(section) {
    var el = section.querySelector('[data-recommended-slider]');
    if (!el || el.swiper) return;
    // No layout yet — the menu column is hidden behind the wishlist layer.
    // Building here would measure zero and latch autoHeight at 0; the slider
    // repair pass picks it up when the column is visible again.
    if (!el.offsetWidth) return;
    if (typeof window.Swiper === 'undefined') {
      if (typeof window.whenSwiperReady === 'function') window.whenSwiperReady(function () { initSlider(section); });
      return;
    }
    new window.Swiper(el, {
      slidesPerView: 2.2,
      spaceBetween: 12,
      autoHeight: true,
      navigation: {
        prevEl: section.querySelector('[data-recommended-prev]'),
        nextEl: section.querySelector('[data-recommended-next]')
      }
    });
  }

  function render(drawer) {
    var section = drawer.querySelector('[data-recommended]');
    if (!section) return;
    var list = section.querySelector('[data-recommended-list]');
    if (!list) return;

    var seeds = seedsFor(section);
    if (!seeds.length) return; // nothing to recommend from — stays hidden

    var cartCount = section.getAttribute('data-recommended-cart-count') || '0';
    firstNonEmpty(seeds, cartCount).then(function (cardsHTML) {
      if (!cardsHTML) return;
      list.innerHTML = cardsHTML;
      if (!list.children.length) return;
      section.hidden = false;
      initSlider(section);
    });
  }

  function loadOnce(drawer) {
    if (loadedDrawers.has(drawer)) return;
    loadedDrawers.add(drawer);
    render(drawer);
  }

  // Warm the first seed as soon as the seeds exist — i.e. the moment the body
  // lands, or on open-intent when the body is already in place. Only the first
  // seed: cascading on a miss is the rare path and isn't worth extra requests.
  function prefetch() {
    if (prefetched) return;
    for (var i = 0; i < DRAWERS.length; i++) {
      var section = DRAWERS[i].el.querySelector('[data-recommended]');
      if (!section) continue;
      var seeds = seedsFor(section);
      if (!seeds.length) continue;
      prefetched = true;
      fetchCardsHTML(seeds[0], section.getAttribute('data-recommended-cart-count') || '0');
      return;
    }
  }

  function onIntent(event) {
    var target = event.target;
    if (target && target.closest && target.closest('[data-side-account-trigger]')) {
      prefetch();
    }
  }
  document.addEventListener('mouseover', onIntent, { passive: true });
  document.addEventListener('focusin', onIntent);
  document.addEventListener('touchstart', onIntent, { passive: true });

  DRAWERS.forEach(function (d) {
    if (d.el.classList.contains(d.openClass)) {
      loadOnce(d.el);
    } else {
      new MutationObserver(function () {
        if (d.el.classList.contains(d.openClass)) loadOnce(d.el);
      }).observe(d.el, { attributes: true, attributeFilter: ['class'] });
    }
  });

  // Fires when the drawer body is fetched on open-intent, and again when a live
  // "Sign in with Shop" login swaps the content — either way the seeds and
  // slider nodes are new. Warm the first seed immediately (the drawer usually
  // isn't open yet) and re-render any drawer that is already open.
  document.addEventListener('account-content:updated', function () {
    prefetched = false;
    prefetch();
    DRAWERS.forEach(function (d) {
      loadedDrawers.delete(d.el);
      if (d.el.classList.contains(d.openClass)) loadOnce(d.el);
    });
  });
})();

/*
 * Friendbuy: render the current customer's coupon count into the wallet
 * section of the account drawer. push(['getCoupons', fn]) is authenticated by
 * the authMessage/authSignature set up in friendbuy.liquid, so the SDK
 * returns data scoped to the signed-in shopper — no bearer token in the
 * browser. (push(['getRewards', fn]) is the broader reward set if we ever
 * need it.)
 */
// (function () {
//   var tries = 0;
//   var iv = setInterval(function () {
//     var api = window.friendbuyAPI;
//     if (api && api.loaded === true && typeof api.push === 'function') {
//       clearInterval(iv);
//       api.push(['getCoupons', function (err, response) {
//         if (err) return;
//         var list = response && response.data && response.data.coupons;
//         var count = Array.isArray(list) ? list.length : 0;
        
//         document.querySelectorAll('[data-coupons-count]').forEach(function (el) {
//           el.textContent = count + (count === 1 ? ' Reward' : ' Rewards');
//           if (el.parentElement) el.parentElement.hidden = false;
//         });
//       }]);
//     } else if (++tries >= 50) {
//       clearInterval(iv);
//     }
//   }, 200);
// })();

